import { fg } from '@atlaskit/platform-feature-flags';
import { useEffect, useState } from 'react';
import { isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';

/**
 * this hook requests / refresh / ensure the signed cookies for IC Forge CDN.
 * It is used only within IC environment, and the aim is to allow IC apps to be accessible.
 *
 * @return boolean - whether the signed cookie is still loading (should wait for it before loading the iframe)
 */
export const useCDNAuthSignedCookie = (): boolean => {
	const [isLoading, setIsLoading] = useState(shouldUseCDNAuth());

	useEffect(() => {
		if (isLoading) {
			loadCDNAuthSignedCookie()
				.catch((err) => {
					// eslint-disable-next-line no-console
					console.error('Error loading CDN auth signed cookie:', err);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [isLoading]);

	return isLoading;
};

const MAX_SIGNED_COOKIE_AGE_MS = 30 * 60 * 1000; // 30 minutes
let lastSignedCookieFetchTime = 0;
let loadingPromise: Promise<void> | null = null;

const doLoadCDNAuthSignedCookie = async () => {
	const icDomain = isolatedCloudDomain();
	const signedCookieUrl = `https://forge.forge-cdn.${icDomain}/forge-cdn/signed-cookies`;
	return await fetch(signedCookieUrl, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

const loadCDNAuthSignedCookie = async () => {
	if (Date.now() - lastSignedCookieFetchTime < MAX_SIGNED_COOKIE_AGE_MS) {
		return;
	}
	// this is to ensure only one loadingPromise is active at a time (to avoid multiple simultaneous requests)
	if (loadingPromise) {
		await loadingPromise;
	} else {
		loadingPromise = (async () => {
			try {
				const response = await doLoadCDNAuthSignedCookie();
				if (!response.ok) {
					throw new Error(`Failed to fetch signed cookies: ${response.statusText}`);
				}
				lastSignedCookieFetchTime = Date.now();
			} finally {
				loadingPromise = null;
			}
		})();

		await loadingPromise;
	}
};

export const shouldUseCDNAuth = (): boolean => {
	return fg('forge-ui-cdn-auth') && isIsolatedCloud();
};

// this is only used in tests to reset the cache
export const resetSignedCookieFetchCache = (): void => {
	lastSignedCookieFetchTime = 0;
	loadingPromise = null;
};
